import AddButton from "components/Button/AddButton";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DropdownStatusList, EmptyString, FormTypeAdd, PageSize } from "utils/Constant";
import Dropdown from "components/Input/Dropdown";
import { GetStatus } from "utils/helpers";
import { useModal } from "utils/context/ModalContext";
import { useCallApi } from "api/apiHooks";
import Pagination from "components/Input/PaginationButton";
import { FilterCard, GridDeleteButton, GridEditButton, MainBodyCard, PagesTitle } from "components/Cards";
import Select from 'react-select'
import DataTable from "components/DataTable";

export default function EDC(){
    let navigate = useNavigate()

    const firstLoad = useRef(false)
    const debounceRef = useRef(null)

    const { showFail, showLoading, hideLoading, showSuccess, confirm } = useModal()
    const { fetchEdcList, fetchEdcBankList, fetchDeleteEdc } = useCallApi()

    const [edcList, setEdcList] = useState([])
    const [bankId, setBankId] = useState(EmptyString)
    const [bankOptions, setBankOptions] = useState([])
    const [edcName, setEdcName] = useState(EmptyString)
    const [beneficiaryAccountNo, setBeneficiaryAccountNo] = useState(EmptyString)
    const [beneficiaryName, setBeneficiaryName] = useState(EmptyString)
    const [status, setStatus] = useState(DropdownStatusList[1].value)
    const [pageIndex, setPageIndex] = useState(0)
    const [totalPages, setTotalPages] = useState(0)

    const columns = [
        {
            key: "rowNumber",
            header: "No",
            headerClassName: "w-16 font-semibold",
            cellClassName: "text-center"
        },
        {
            key: "edcName",
            header: "EDC Name",
            render: (row) => row.bankCode + ' - ' + row.edcName,
        },
        {
            key: "typeDesc",
            header: "Type",
        },
        {
            key: "mdrOn",
            header: "Credit On Us",
        },
        {
            key: "mdrOff",
            header: "Credit Off Us",
        },
        {
            key: "mdrCreditAmex",
            header: "Credit Amex",
        },
        {
            key: "mdrDebitOnUs",
            header: "Debit On Us",
        },
        {
            key: "mdrDebitOffUs",
            header: "Debit Off Us",
        },
        {
            key: "mdrQrisOn",
            header: "Qris On Us",
        },
        {
            key: "mdrQrisOff",
            header: "Qris Off Us",
        },
        {
            key: "status",
            header: "Status",
            render: (row) => GetStatus(row.status),
        },
        {
            key: "action",
            header: "Action",
            render: (row) => (
                <div className="flex gap-2">
                    <GridEditButton isActive={row.status} onClick={() => editBtnClick(row.edcId)} size="w-3" />
                    <GridDeleteButton isActive={row.status} onClick={() => deleteBtnClick(row.edcId, row.status)} size="w-3" />
                </div>
            ),
        },
    ]

    async function handlePageChange(newIndex) {
        try {
            showLoading()

            const res = await fetchEdcList(
                bankId,
                edcName, 
                beneficiaryAccountNo,
                beneficiaryName,
                status,
                PageSize,
                parseInt(newIndex)
            )

            if (!res) return

            const totalData = res?.data?.totalData ?? 0;
            const total = Math.max(1, Math.ceil(totalData / PageSize));

            setEdcList(res)
            setTotalPages(total);
            setPageIndex(newIndex);
        } finally {
            hideLoading();
        }
    }

    const addBtnClick = () => {
        navigate("/setting/edc/form?edcId=&formType=" + FormTypeAdd)
    }
    const editBtnClick = (edcId) => {
        navigate("/setting/edc/form?edcId=" + edcId + "&formType=Edit")
    }
    const deleteBtnClick = async (edcId, status) => {
        const isConfirmed = await confirm()

        if (!isConfirmed) return 

        showLoading()
        
        try {
            const res = await fetchDeleteEdc(edcId, status)
            if (!res) return
        }
        finally {
            hideLoading()
            showSuccess({
                onSuccess: () => {
                    handlePageChange(pageIndex)
                }
            })
        }
    }

    const getBankList = async () => {
        const res = await fetchEdcBankList()
        if (!res) return

        const data = res.data.listData
        const arr = [
            { label: "All", value: "" },
            ...data.map(item => ({
                label: `${item.bankCode} - ${item.bankName}`,
                value: String(item.bankId)
            }))
        ]

        setBankOptions(arr)
        setBankId(arr[0].value)
    }

    useEffect(() => {
        const onLoad = async () => {
            if (firstLoad.current) {
                await getBankList()
                handlePageChange(0)

                firstLoad.current = false
            } 
            else {
                if (debounceRef.current) {
                    clearTimeout(debounceRef.current)
                }
        
                debounceRef.current = setTimeout(() => {
                    handlePageChange(0)
                }, 500)
            }
        }

        onLoad()
    },[bankId, edcName, status])

    return(
        <MainBodyCard>
            <PagesTitle title='EDC' />

            <div className="flex flex-col sm:flex-row gap-5 justify-between mt-3">
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 w-full">
                    <FilterCard title='Bank' width='w-full sm:w-72'>
                        <Select className='w-full' value={bankOptions.find(x => x.bankId === bankId)} options={bankOptions} onChange={(e) => setBankId(e.value)} placeholder={"All"} />
                    </FilterCard>
                    <FilterCard title='EDC Name' width='w-full sm:w-72'>
                        <input className="input" value={edcName} onChange={(e) => setEdcName(e.target.value.toUpperCase())} />
                    </FilterCard>
                    <FilterCard title='Status' width='w-full sm:w-72'>
                        <Dropdown className='w-full' value={status} options={DropdownStatusList} onChange={(e) => setStatus(e.value)} placeholder={"All"} />
                    </FilterCard>
                </div>
            </div>

            <div className="flex w-full justify-end">
                <AddButton onClick={addBtnClick} title="Tambah EDC" />
            </div>
           
            <div className="mt-5">
                <DataTable
                    columns={columns}
                    data={edcList?.data?.listData ?? []}
                />
            </div>

            <Pagination pageIndex={pageIndex} totalPages={totalPages} onPageChange={handlePageChange} />
        </MainBodyCard>
    )
}